import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AppWindow } from 'lucide-react';
import { listDepartments } from '@/api/departments';
import { addDepartmentSoftware, listDepartmentSoftware, removeDepartmentSoftware, updateDepartmentSoftware } from '@/api/departmentSoftware';
import { listSoftware, linkSoftwarePool, unlinkSoftwarePool } from '@/api/software';
import { ApiError } from '@/api/client';
import { useAuth } from '@/auth/useAuth';
import { AlertDialog } from '@/components/ui/AlertDialog';
import {
  CoverageHint, CoveragePanel, LinkedBadge, ResizablePalette, SelectRow, type CoverageItem,
} from '@/components/common/allocationPanel';
import type { Department, DepartmentSoftware, SoftwarePool } from '@/types/oscar';

const ALLOC_KEY = ['departmentSoftware'] as const;

type View = 'department' | 'software';
// By-software palette entry: a standalone pool, or a mirror group of linked pools
// rendered as one clustered block.
type PaletteEntry =
  | { kind: 'single'; pool: SoftwarePool }
  | { kind: 'group'; groupId: string; members: SoftwarePool[] };


/**
 * Onboarding — Software Allocation. Two symmetric lenses over the same
 * oscar.DepartmentSoftwarePool links; both use the same pattern — pick an item
 * on the left, and the right panel splits the other side into Allocated / Not
 * allocated (filterable + searchable) with inline Allocate / remove / Mandatory
 * toggle actions:
 *   • By software (default) — pick a pool → its department coverage.
 *   • By department — pick a department → its software coverage.
 * Admin-only for mutation; everyone else sees the split read-only.
 */
export function SoftwareAllocation() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();

  const [view, setView] = useState<View>('software');
  const [removeTarget, setRemoveTarget] = useState<DepartmentSoftware | null>(null);

  const departmentsQuery = useQuery({
    queryKey: ['departments', { includeDeleted: false }],
    queryFn: ({ signal }) => listDepartments(false, signal),
  });
  const allocationsQuery = useQuery({
    queryKey: ALLOC_KEY,
    queryFn: ({ signal }) => listDepartmentSoftware(undefined, signal),
  });
  const softwareQuery = useQuery({
    queryKey: ['software'],
    queryFn: ({ signal }) => listSoftware(signal),
  });

  const departments = useMemo(() => departmentsQuery.data ?? [], [departmentsQuery.data]);
  const allocations = useMemo(() => allocationsQuery.data ?? [], [allocationsQuery.data]);
  const pools = useMemo(() => softwareQuery.data ?? [], [softwareQuery.data]);

  const allocByCode = useMemo(() => {
    const map = new Map<string, DepartmentSoftware[]>();
    for (const a of allocations) {
      const arr = map.get(a.DepartmentCode) ?? [];
      arr.push(a);
      map.set(a.DepartmentCode, arr);
    }
    return map;
  }, [allocations]);

  const allocByPool = useMemo(() => {
    const map = new Map<number, DepartmentSoftware[]>();
    for (const a of allocations) {
      const arr = map.get(a.SoftwarePoolId) ?? [];
      arr.push(a);
      map.set(a.SoftwarePoolId, arr);
    }
    return map;
  }, [allocations]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ALLOC_KEY });

  const addMutation = useMutation({
    mutationFn: ({ code, poolId, mandatory }: { code: string; poolId: number; mandatory: boolean }) =>
      addDepartmentSoftware(code, { SoftwarePoolId: poolId, IsMandatory: mandatory }),
    onSuccess: invalidate,
  });
  const toggleMutation = useMutation({
    mutationFn: ({ id, mandatory }: { id: number; mandatory: boolean }) =>
      updateDepartmentSoftware(id, { IsMandatory: mandatory }),
    onSuccess: invalidate,
  });
  // Opening the confirm clears any error left by the previous attempt.
  const openRemove = (a: DepartmentSoftware) => { removeMutation.reset(); setRemoveTarget(a); };
  const removeMutation = useMutation({
    mutationFn: (id: number) => removeDepartmentSoftware(id),
    onSuccess: () => { invalidate(); setRemoveTarget(null); },
  });
  // Linking changes both the pools (LinkGroupId) and, via reconcile/cascade, the
  // allocations — so refresh both queries on success.
  const invalidateBoth = () => { queryClient.invalidateQueries({ queryKey: ['software'] }); invalidate(); };
  const linkMutation = useMutation({
    mutationFn: ({ id, otherId }: { id: number; otherId: number }) => linkSoftwarePool(id, otherId),
    onSuccess: invalidateBoth,
  });
  const unlinkMutation = useMutation({
    mutationFn: (id: number) => unlinkSoftwarePool(id),
    onSuccess: invalidateBoth,
  });

  const mutationError = addMutation.error ?? toggleMutation.error ?? removeMutation.error ?? linkMutation.error ?? unlinkMutation.error;
  const onAdd = (code: string, poolId: number) => addMutation.mutate({ code, poolId, mandatory: true });
  const onToggleMandatory = (a: DepartmentSoftware) => toggleMutation.mutate({ id: a.DepartmentSoftwarePoolId, mandatory: !a.IsMandatory });
  const onLink = (id: number, otherId: number) => linkMutation.mutate({ id, otherId });
  const onUnlink = (id: number) => unlinkMutation.mutate(id);

  const loading = departmentsQuery.isLoading || allocationsQuery.isLoading || softwareQuery.isLoading;
  const errored = departmentsQuery.isError || allocationsQuery.isError || softwareQuery.isError;

  return (
    <section>
      <header className="mb-5">
        <h1 className="flex items-center gap-2 font-heading text-2xl font-bold text-navy">
          <AppWindow className="h-6 w-6 text-navy" aria-hidden /> Software Allocation
        </h1>
        <p className="mt-1 text-sm text-darkGrey/80">
          {view === 'software'
            ? (isAdmin
                ? 'Pick a software item; the right panel shows which departments are Allocated vs Not allocated (filter + search). Allocate a department, remove it, or toggle Mandatory / Optional there.'
                : 'Pick a software item to see which departments it is allocated to (and which it is not).')
            : (isAdmin
                ? 'Pick a department; the right panel shows which software is Allocated vs Not allocated (filter + search). Allocate a pool, remove it, or toggle Mandatory / Optional there.'
                : 'Pick a department to see which software it is entitled to (and which it is not).')}
        </p>
        <div className="mt-3 inline-flex rounded border border-border bg-white p-0.5 text-sm" role="tablist" aria-label="Allocation view">
          <button
            type="button"
            role="tab"
            aria-selected={view === 'software'}
            onClick={() => setView('software')}
            className={view === 'software' ? 'rounded bg-navy px-3 py-1 font-medium text-white' : 'rounded px-3 py-1 font-medium text-darkGrey/70 hover:text-navy'}
          >
            By software
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === 'department'}
            onClick={() => setView('department')}
            className={view === 'department' ? 'rounded bg-navy px-3 py-1 font-medium text-white' : 'rounded px-3 py-1 font-medium text-darkGrey/70 hover:text-navy'}
          >
            By department
          </button>
        </div>
      </header>

      {mutationError && (
        <div role="alert" className="mb-3 rounded border border-red/30 bg-red/10 px-3 py-2 text-sm text-red">
          {mutationError instanceof ApiError ? mutationError.message : 'Could not update the software allocation.'}
        </div>
      )}

      {loading && <p className="mt-6 text-sm text-darkGrey/60">Loading…</p>}
      {errored && <p className="mt-6 text-sm text-red">Could not load the allocations.</p>}

      {!loading && !errored && view === 'software' && (
        <SoftwareView
          pools={pools}
          allocByPool={allocByPool}
          departments={departments}
          admin={isAdmin}
          onAdd={onAdd}
          onToggleMandatory={onToggleMandatory}
          onRemove={openRemove}
          onLink={onLink}
          onUnlink={onUnlink}
        />
      )}

      {!loading && !errored && view === 'department' && (
        <DepartmentView
          pools={pools}
          allocByCode={allocByCode}
          departments={departments}
          admin={isAdmin}
          onAdd={onAdd}
          onToggleMandatory={onToggleMandatory}
          onRemove={openRemove}
        />
      )}

      {removeTarget && (
        <AlertDialog
          open={removeTarget !== null}
          onOpenChange={(o) => { if (!o) setRemoveTarget(null); }}
          title={`Remove "${removeTarget.Title}"?`}
          description={`This removes ${removeTarget.Title} from ${removeTarget.DepartmentCode}'s software entitlement. You can add it back later.`}
          confirmLabel="Remove"
          destructive
          closeOnConfirm={false}
          onConfirm={() => removeMutation.mutate(removeTarget.DepartmentSoftwarePoolId)}
        >
          {removeMutation.isError && (
            <p role="alert" className="text-sm text-red">
              {removeMutation.error instanceof ApiError ? removeMutation.error.message : 'Could not remove that allocation.'}
            </p>
          )}
        </AlertDialog>
      )}
    </section>
  );
}

// ── By software: pick a pool → department coverage ──────────────────────────

function SoftwareView({
  pools,
  allocByPool,
  departments,
  admin,
  onAdd,
  onToggleMandatory,
  onRemove,
  onLink,
  onUnlink,
}: {
  pools: SoftwarePool[];
  allocByPool: Map<number, DepartmentSoftware[]>;
  departments: Department[];
  admin: boolean;
  onAdd: (code: string, poolId: number) => void;
  onToggleMandatory: (a: DepartmentSoftware) => void;
  onRemove: (a: DepartmentSoftware) => void;
  onLink: (id: number, otherId: number) => void;
  onUnlink: (id: number) => void;
}) {
  const [q, setQ] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return term ? pools.filter((p) => `${p.Title} ${p.SKU ?? ''}`.toLowerCase().includes(term)) : pools;
  }, [pools, q]);

  // Cluster linked pools together: pools sharing a non-null LinkGroupId become a
  // single group block (members indented under a "Linked group" heading), unlinked
  // pools stay standalone. Both are ordered alphabetically — a group sorts by its
  // first member's title so its rows sit adjacent instead of scattered through the
  // A–Z list. Mirror groups are symmetric (no master pool), so the block has no
  // designated header pool. Grouping runs after the search filter.
  const entries = useMemo<PaletteEntry[]>(() => {
    const groups = new Map<string, SoftwarePool[]>();
    const singles: SoftwarePool[] = [];
    for (const p of filtered) {
      if (p.LinkGroupId) {
        const arr = groups.get(p.LinkGroupId) ?? [];
        arr.push(p);
        groups.set(p.LinkGroupId, arr);
      } else {
        singles.push(p);
      }
    }
    const keyed: { key: string; entry: PaletteEntry }[] = singles.map((pool) => ({
      key: pool.Title.toLowerCase(),
      entry: { kind: 'single', pool },
    }));
    for (const [groupId, members] of groups) {
      members.sort((a, b) => a.Title.localeCompare(b.Title));
      keyed.push({ key: members[0].Title.toLowerCase(), entry: { kind: 'group', groupId, members } });
    }
    keyed.sort((a, b) => a.key.localeCompare(b.key));
    return keyed.map((k) => k.entry);
  }, [filtered]);

  const selectedPool = useMemo(() => pools.find((p) => p.SoftwarePoolId === selectedId) ?? null, [pools, selectedId]);
  // Linked siblings of the selected pool — same non-null LinkGroupId (client-side
  // from the already-loaded pools; no extra fetch).
  const siblings = useMemo(
    () => (selectedPool?.LinkGroupId ? pools.filter((p) => p.LinkGroupId === selectedPool.LinkGroupId && p.SoftwarePoolId !== selectedPool.SoftwarePoolId) : []),
    [pools, selectedPool],
  );

  // Build one coverage row per department for the selected pool.
  const items = useMemo<CoverageItem<DepartmentSoftware>[]>(() => {
    if (!selectedPool) return [];
    const allocByDept = new Map((allocByPool.get(selectedPool.SoftwarePoolId) ?? []).map((a) => [a.DepartmentCode, a] as const));
    return departments.map((d) => ({
      key: d.DepartmentCode,
      code: d.DepartmentCode,
      label: d.DepartmentDescription,
      mono: true,
      search: `${d.DepartmentCode} ${d.DepartmentDescription}`.toLowerCase(),
      alloc: allocByDept.get(d.DepartmentCode) ?? null,
      targetId: selectedPool.SoftwarePoolId,
    }));
  }, [selectedPool, allocByPool, departments]);

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      <div className="flex-1">
        <label className="mb-3 flex flex-col text-xs font-semibold text-darkGrey">
          Search software
          <input aria-label="Search software" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Title or SKU" className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal" />
        </label>
        <ul className="space-y-2">
          {entries.map((entry) =>
            entry.kind === 'single' ? (
              <SelectRow
                key={entry.pool.SoftwarePoolId}
                primary={entry.pool.Title}
                secondary={`(${entry.pool.LicenceType})${entry.pool.SKU ? ` · ${entry.pool.SKU}` : ''}`}
                count={allocByPool.get(entry.pool.SoftwarePoolId)?.length ?? 0}
                selected={entry.pool.SoftwarePoolId === selectedId}
                onSelect={() => setSelectedId(entry.pool.SoftwarePoolId)}
              />
            ) : (
              <LinkGroupBlock
                key={entry.groupId}
                members={entry.members}
                allocByPool={allocByPool}
                selectedId={selectedId}
                onSelect={setSelectedId}
              />
            ),
          )}
          {entries.length === 0 && <li className="text-sm text-darkGrey/60">No software pools match.</li>}
        </ul>
      </div>

      <ResizablePalette>
        {selectedPool ? (
          <div className="space-y-3">
            <LinkedPoolsStrip pool={selectedPool} siblings={siblings} pools={pools} admin={admin} onLink={onLink} onUnlink={onUnlink} />
            <CoveragePanel
              title={<>Coverage — <span className="text-navy">{selectedPool.Title}</span></>}
              searchPlaceholder="Search departments"
              items={items}
              admin={admin}
              onAllocate={(it) => onAdd(it.code, it.targetId)}
              renderAllocatedExtra={(a) => <MandatoryToggle alloc={a} admin={admin} onToggle={onToggleMandatory} />}
              onRemove={onRemove}
            />
          </div>
        ) : (
          <CoverageHint>Select a software item on the left to see which departments are allocated to it — and which aren’t.</CoverageHint>
        )}
      </ResizablePalette>
    </div>
  );
}

// ── By department: pick a department → software coverage ────────────────────

function DepartmentView({
  pools,
  allocByCode,
  departments,
  admin,
  onAdd,
  onToggleMandatory,
  onRemove,
}: {
  pools: SoftwarePool[];
  allocByCode: Map<string, DepartmentSoftware[]>;
  departments: Department[];
  admin: boolean;
  onAdd: (code: string, poolId: number) => void;
  onToggleMandatory: (a: DepartmentSoftware) => void;
  onRemove: (a: DepartmentSoftware) => void;
}) {
  const [q, setQ] = useState('');
  const [selectedCode, setSelectedCode] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return term ? departments.filter((d) => `${d.DepartmentCode} ${d.DepartmentDescription}`.toLowerCase().includes(term)) : departments;
  }, [departments, q]);

  const selectedDept = useMemo(() => departments.find((d) => d.DepartmentCode === selectedCode) ?? null, [departments, selectedCode]);

  // Build one coverage row per software pool for the selected department.
  const items = useMemo<CoverageItem<DepartmentSoftware>[]>(() => {
    if (!selectedDept) return [];
    const allocByPoolId = new Map((allocByCode.get(selectedDept.DepartmentCode) ?? []).map((a) => [a.SoftwarePoolId, a] as const));
    return pools.map((p) => ({
      key: String(p.SoftwarePoolId),
      code: p.Title,
      label: `(${p.LicenceType})${p.SKU ? ` · ${p.SKU}` : ''}`,
      mono: false,
      linked: p.LinkGroupId != null,
      search: `${p.Title} ${p.SKU ?? ''}`.toLowerCase(),
      alloc: allocByPoolId.get(p.SoftwarePoolId) ?? null,
      targetId: p.SoftwarePoolId,
    }));
  }, [selectedDept, allocByCode, pools]);

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      <div className="flex-1">
        <label className="mb-3 flex flex-col text-xs font-semibold text-darkGrey">
          Search departments
          <input aria-label="Search departments" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Code or description" className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal" />
        </label>
        <ul className="space-y-2">
          {filtered.map((d) => (
            <SelectRow
              key={d.DepartmentCode}
              primary={d.DepartmentCode}
              secondary={d.DepartmentDescription}
              mono
              count={allocByCode.get(d.DepartmentCode)?.length ?? 0}
              selected={d.DepartmentCode === selectedCode}
              onSelect={() => setSelectedCode(d.DepartmentCode)}
            />
          ))}
          {filtered.length === 0 && <li className="text-sm text-darkGrey/60">No departments match.</li>}
        </ul>
      </div>

      <ResizablePalette>
        {selectedDept ? (
          <CoveragePanel
            title={<>Coverage — <span className="font-mono text-navy">{selectedDept.DepartmentCode}</span> <span className="text-navy">{selectedDept.DepartmentDescription}</span></>}
            searchPlaceholder="Search software"
            items={items}
            admin={admin}
            onAllocate={(it) => onAdd(selectedDept.DepartmentCode, it.targetId)}
            renderAllocatedExtra={(a) => <MandatoryToggle alloc={a} admin={admin} onToggle={onToggleMandatory} />}
            onRemove={onRemove}
          />
        ) : (
          <CoverageHint>Select a department on the left to see which software is allocated to it — and which isn’t.</CoverageHint>
        )}
      </ResizablePalette>
    </div>
  );
}







// A selectable row for the left column of either view.

// A clustered block for a mirror group in the By-software palette: the linked
// pools grouped together, indented under a shared "Linked group" heading. The
// group has no master pool (mirror groups are symmetric), so the heading is
// generic; each member row stays individually selectable and drops the per-row
// "Linked" badge since the block itself carries it.
function LinkGroupBlock({ members, allocByPool, selectedId, onSelect }: {
  members: SoftwarePool[];
  allocByPool: Map<number, DepartmentSoftware[]>;
  selectedId: number | null;
  onSelect: (id: number) => void;
}) {
  return (
    <li className="rounded border border-lightBlue bg-lightBlue/20 p-1.5">
      <div className="mb-1.5 flex flex-wrap items-center gap-1.5 px-1.5 pt-0.5">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-navy/70">Linked group</span>
        <LinkedBadge />
        <span className="text-[11px] text-darkGrey/50">{members.length} pools · allocations mirror</span>
      </div>
      <ul className="space-y-1.5 border-l-2 border-navy/20 pl-2">
        {members.map((p) => (
          <SelectRow
            key={p.SoftwarePoolId}
            primary={p.Title}
            secondary={`(${p.LicenceType})${p.SKU ? ` · ${p.SKU}` : ''}`}
            count={allocByPool.get(p.SoftwarePoolId)?.length ?? 0}
            selected={p.SoftwarePoolId === selectedId}
            onSelect={() => onSelect(p.SoftwarePoolId)}
          />
        ))}
      </ul>
    </li>
  );
}

// ── Linked-pools strip (link / unlink from the allocation screen) ───────────

function LinkedPoolsStrip({ pool, siblings, pools, admin, onLink, onUnlink }: {
  pool: SoftwarePool;
  siblings: SoftwarePool[];
  pools: SoftwarePool[];
  admin: boolean;
  onLink: (id: number, otherId: number) => void;
  onUnlink: (id: number) => void;
}) {
  const [pick, setPick] = useState('');
  // Linkable = every other pool not already in this pool's group.
  const groupIds = new Set([pool.SoftwarePoolId, ...siblings.map((s) => s.SoftwarePoolId)]);
  const options = pools.filter((p) => !groupIds.has(p.SoftwarePoolId));

  if (!admin && siblings.length === 0) return null;

  return (
    <div className="rounded border border-border bg-white p-3">
      <div className="flex items-center gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-darkGrey/70">Linked pools</h3>
        {siblings.length > 0 && <LinkedBadge />}
      </div>
      {siblings.length > 0 ? (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {siblings.map((s) => (
            <li key={s.SoftwarePoolId} className="rounded border border-border bg-bg/50 px-2 py-0.5 text-xs text-darkGrey/80">{s.Title}</li>
          ))}
        </ul>
      ) : (
        <p className="mt-1 text-xs text-darkGrey/60">Not linked. Allocations for this pool stand alone.</p>
      )}
      {admin && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <select
            aria-label="Link another pool"
            value={pick}
            onChange={(e) => { const v = e.target.value; setPick(''); if (v) onLink(pool.SoftwarePoolId, Number(v)); }}
            className="rounded border border-border bg-white px-2 py-1 text-xs"
          >
            <option value="">Link another pool…</option>
            {options.map((p) => <option key={p.SoftwarePoolId} value={p.SoftwarePoolId}>{p.Title}</option>)}
          </select>
          {siblings.length > 0 && (
            <button type="button" onClick={() => onUnlink(pool.SoftwarePoolId)} className="rounded border border-red/40 bg-white px-2 py-1 text-xs font-medium text-red hover:bg-red/10">Unlink this pool</button>
          )}
        </div>
      )}
      {admin && <p className="mt-1 text-[11px] text-darkGrey/50">Linking mirrors department allocations across the group (reconciled to the union). Unlinking keeps existing allocations.</p>}
    </div>
  );
}

// ── Shared Mandatory/Optional toggle ────────────────────────────────────────

function MandatoryToggle({ alloc, admin, onToggle }: { alloc: DepartmentSoftware; admin: boolean; onToggle: (a: DepartmentSoftware) => void }) {
  return (
    <button
      type="button"
      disabled={!admin}
      onClick={() => onToggle(alloc)}
      aria-label={`${alloc.Title} is ${alloc.IsMandatory ? 'mandatory' : 'optional'} — toggle`}
      className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${alloc.IsMandatory ? 'bg-orange/15 text-orange' : 'bg-darkGrey/10 text-darkGrey'} ${admin ? 'hover:opacity-80' : 'cursor-default'}`}
    >
      {alloc.IsMandatory ? 'Mandatory' : 'Optional'}
    </button>
  );
}

// ── Resizable right-hand panel shell ────────────────────────────────────────

/**
 * ResizablePalette — wraps the right-hand coverage panel and gives it a
 * draggable left-edge handle so the user can widen it; the chosen width is
 * remembered per browser. Applied only at lg+ (via a CSS variable on an
 * `lg:w-[var(--palette-w)]` utility) — below lg the column stacks full-width and
 * the handle is hidden. Keyboard: focus the handle and use ←/→ (← widens, since
 * the handle is on the left edge) in 24px steps.
 */
